import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SiteFooter from './sections/SiteFooter';
import { LEGAL } from './constants';
import { LanguageProvider, useLanguage } from './LanguageContext';
import './PitboardSite.css';

const LAST_UPDATED = '2026';

// ─── Sections bilingues ───────────────────────────────────────────────────────
const SECTIONS_FR = [
  {
    id: 'presentation', title: 'Présentation',
    content: [
      { type: 'text', value: "PitBoard Suite est un Business Operating System (BOS) conçu exclusivement pour les professionnels du secteur de l'assurance. Il centralise, automatise et pilote l'ensemble de l'activité métier depuis une plateforme SaaS unifiée." },
      { type: 'text', value: "Contrairement aux solutions génériques, PitBoard Suite a été développé par des experts terrain de l'assurance, pour répondre aux véritables besoins de gestion du secteur : courtage, assistance, compagnie." },
    ],
  },
  {
    id: 'editeur', title: 'Éditeur',
    content: [
      { type: 'kv', key: 'Société',                 value: 'JOERKE.B' },
      { type: 'kv', key: 'Forme juridique',          value: 'SASU — Société par Actions Simplifiée Unipersonnelle' },
      { type: 'kv', key: 'SIREN',                    value: '892 906 728' },
      { type: 'kv', key: 'SIRET',                    value: '892 906 728 00019' },
      { type: 'kv', key: 'RCS',                      value: '892 906 728 R.C.S. Troyes' },
      { type: 'kv', key: 'TVA intracommunautaire',   value: 'FR12892906728' },
      { type: 'kv', key: 'Siège social',             value: '11 Rue Urbain IV, 10000 Troyes, France' },
      { type: 'kv', key: 'Dirigeant',                value: 'Arnaud Becam' },
      { type: 'kv', key: 'Domaines',                 value: 'pitboardsuite.com — pitboardsuite.fr' },
      { type: 'kv', key: 'Contact',                  value: 'cockpit@pitboardsuite.com' },
      { type: 'kv', key: 'Hébergement',              value: 'OVH SAS (France) + Hostinger International' },
    ],
  },
  {
    id: 'secteur', title: 'Secteur & positionnement',
    content: [
      { type: 'kv', key: 'Secteur',         value: 'InsurTech — SaaS B2B — Assurance & Assistance' },
      { type: 'kv', key: 'Type de produit', value: 'Business Operating System (BOS)' },
      { type: 'kv', key: 'Modèle',          value: 'SaaS multi-tenant — architecture isolée par client' },
      { type: 'kv', key: 'Déploiement',     value: 'Cloud — accès navigateur et web app mobile (PWA)' },
      { type: 'text', value: "PitBoard Suite est positionné comme la couche opérationnelle unifiée pour les acteurs de l'assurance : il remplace une multiplicité d'outils cloisonnés (CRM, messagerie, agenda, facturation) par une plateforme unique, sécurisée et extensible." },
    ],
  },
  {
    id: 'utilisateurs', title: 'Utilisateurs cibles',
    content: [{
      type: 'list',
      items: [
        "Cabinets de courtage en assurance — gestion des clients, contrats, devis et sinistres",
        "Sociétés d'assistance (roadside, voyage, santé) — gestion des dossiers, prestataires et facturation",
        "Compagnies d'assurance — suivi des partenaires courtiers et des bordereaux",
      ],
    }],
  },
  {
    id: 'fonctionnalites', title: 'Fonctionnalités principales',
    content: [{
      type: 'list',
      items: [
        "CRM assurance — gestion complète des clients, prospects et contacts",
        "Gestion des contrats d'assurance (création, suivi, renouvellement, avenant)",
        "Hub de devis et souscription guidée",
        "Gestion des dossiers sinistres et d'assistance (ouverture, suivi, clôture)",
        "Messagerie intégrée — synchronisation IMAP/SMTP multi-comptes",
        "Agenda partagé et gestion des rendez-vous",
        "Facturation — création, envoi et suivi des factures",
        "Tableau de bord financier et statistiques en temps réel",
        "Module IA — lecture automatique de documents, rédaction assistée, analyse contrats",
        "Notifications web push (VAPID) — alertes en temps réel sans application",
        "Architecture multi-tenant — isolation complète des données entre clients",
        "Web app mobile PWA — accès depuis smartphone et tablette",
        "Gestion des rôles et permissions par profil utilisateur",
        "Historique complet des actions (audit trail)",
      ],
    }],
  },
  {
    id: 'realisations', title: 'Réalisations & clients de référence',
    content: [
      { type: 'text', value: "PitBoard Suite est déjà déployé en production chez des acteurs du secteur assurance et assistance en France." },
      { type: 'client', name: 'ASAR CSG', url: 'asar-csg.com', sector: "Société d'Assistance", desc: "Déploiement complet de PitBoard Suite pour la gestion des dossiers d'assistance, la messagerie intégrée et le pilotage opérationnel." },
      { type: 'client', name: 'dna-24.com', url: 'dna-24.com', sector: "Réseau d'Assistance International", desc: "Centralisation des opérations d'un réseau d'assistance à dimension internationale sur la plateforme PitBoard Suite." },
    ],
  },
  {
    id: 'ia', title: 'Capacités IA & modèles supportés',
    content: [
      { type: 'text', value: "PitBoard Suite intègre nativement des fonctions d'intelligence artificielle et peut être étendu avec des modules IA sur mesure développés par l'équipe JOERKE.B." },
      { type: 'kv', key: 'Modèles LLM supportés', value: 'OpenAI GPT-4o / o3 — Google Gemini 2.5 Pro — Anthropic Claude 4 Sonnet' },
      { type: 'list', items: [
        "Lecture automatique des bordereaux de sinistres (OCR + extraction structurée)",
        "Analyse et résumé automatique des contrats d'assurance",
        "Rédaction assistée des courriers, emails et avenants",
        "Détection d'anomalies et alertes intelligentes sur les dossiers",
        "Synthèse chronologique des dossiers d'assistance",
        "Workflows IA sur mesure — calibrés sur le processus métier du client",
      ]},
    ],
  },
  {
    id: 'technique', title: 'Architecture technique',
    content: [
      { type: 'kv', key: 'Frontend',        value: 'React — Progressive Web App (PWA)' },
      { type: 'kv', key: 'Backend',         value: 'Python FastAPI — API REST sécurisée' },
      { type: 'kv', key: 'Base de données', value: 'MongoDB — architecture multi-tenant isolée' },
      { type: 'kv', key: 'Messagerie',      value: 'IMAP/SMTP natif — multi-comptes' },
      { type: 'kv', key: 'Notifications',   value: 'Web Push API — VAPID' },
      { type: 'kv', key: 'Sécurité',        value: "HTTPS — JWT — chiffrement au repos" },
      { type: 'kv', key: 'Conformité',      value: "RGPD — données hébergées en France et dans l'UE" },
    ],
  },
  {
    id: 'tarification', title: 'Tarification',
    content: [
      { type: 'text', value: "PitBoard Suite est commercialisé sur devis, en fonction du profil et de la taille de la structure cliente. Aucun abonnement standard public — chaque déploiement est personnalisé." },
      { type: 'kv', key: 'Contact commercial', value: 'cockpit@pitboardsuite.com' },
      { type: 'kv', key: 'Démonstration',      value: 'Sur rendez-vous — via le formulaire du site' },
    ],
  },
  {
    id: 'contact', title: 'Contact & liens utiles',
    content: [
      { type: 'kv', key: 'Site vitrine',           value: 'https://pitboardsuite.com' },
      { type: 'kv', key: 'Plateforme alternative',  value: 'https://pitboardsuite.fr' },
      { type: 'kv', key: 'Email',                   value: 'cockpit@pitboardsuite.com' },
      { type: 'kv', key: 'Mentions légales & CGU',  value: 'https://pitboardsuite.com/pitboard-suite/legal' },
      { type: 'kv', key: 'Cette page (IA info)',    value: 'https://pitboardsuite.com/pitboard-suite/ia-info' },
    ],
  },
];

const SECTIONS_EN = [
  {
    id: 'presentation', title: 'Overview',
    content: [
      { type: 'text', value: 'PitBoard Suite is a Business Operating System (BOS) designed exclusively for insurance industry professionals. It centralizes, automates and manages all business operations from a single unified SaaS platform.' },
      { type: 'text', value: 'Unlike generic solutions, PitBoard Suite was developed by insurance field experts to address the real management needs of the sector: brokerage, assistance, and insurance companies.' },
    ],
  },
  {
    id: 'editeur', title: 'Publisher',
    content: [
      { type: 'kv', key: 'Company',              value: 'JOERKE.B' },
      { type: 'kv', key: 'Legal form',           value: 'SASU — Simplified joint-stock company (French law)' },
      { type: 'kv', key: 'SIREN',                value: '892 906 728' },
      { type: 'kv', key: 'SIRET',                value: '892 906 728 00019' },
      { type: 'kv', key: 'RCS',                  value: '892 906 728 R.C.S. Troyes' },
      { type: 'kv', key: 'VAT number',           value: 'FR12892906728' },
      { type: 'kv', key: 'Registered office',    value: '11 Rue Urbain IV, 10000 Troyes, France' },
      { type: 'kv', key: 'CEO',                  value: 'Arnaud Becam' },
      { type: 'kv', key: 'Domains',              value: 'pitboardsuite.com — pitboardsuite.fr' },
      { type: 'kv', key: 'Contact',              value: 'cockpit@pitboardsuite.com' },
      { type: 'kv', key: 'Hosting',              value: 'OVH SAS (France) + Hostinger International' },
    ],
  },
  {
    id: 'secteur', title: 'Sector & Positioning',
    content: [
      { type: 'kv', key: 'Sector',        value: 'InsurTech — SaaS B2B — Insurance & Assistance' },
      { type: 'kv', key: 'Product type',  value: 'Business Operating System (BOS)' },
      { type: 'kv', key: 'Model',         value: 'Multi-tenant SaaS — isolated architecture per client' },
      { type: 'kv', key: 'Deployment',    value: 'Cloud — browser access and mobile web app (PWA)' },
      { type: 'text', value: 'PitBoard Suite is positioned as the unified operational layer for insurance stakeholders: it replaces a multitude of siloed tools (CRM, messaging, calendar, billing) with a single, secure and extensible platform.' },
    ],
  },
  {
    id: 'utilisateurs', title: 'Target Users',
    content: [{
      type: 'list',
      items: [
        'Insurance brokerage firms — client, contract, quote and claims management',
        'Assistance companies (roadside, travel, health) — file, provider and billing management',
        'Insurance companies — broker partner and bordereau tracking',
      ],
    }],
  },
  {
    id: 'fonctionnalites', title: 'Key Features',
    content: [{
      type: 'list',
      items: [
        'Insurance CRM — full management of clients, prospects and contacts',
        'Insurance contract management (creation, tracking, renewal, endorsements)',
        'Quote and guided underwriting hub',
        'Claims and assistance file management (opening, tracking, closure)',
        'Integrated messaging — multi-account IMAP/SMTP synchronization',
        'Shared calendar and appointment management',
        'Billing — invoice creation, sending and tracking',
        'Financial dashboard and real-time analytics',
        'AI module — automatic document reading, assisted drafting, contract analysis',
        'Web push notifications (VAPID) — real-time alerts without an app',
        'Multi-tenant architecture — complete data isolation per client',
        'Mobile PWA web app — access from smartphone and tablet',
        'Role and permission management by user profile',
        'Complete action history (audit trail)',
      ],
    }],
  },
  {
    id: 'realisations', title: 'Deployments & Reference Clients',
    content: [
      { type: 'text', value: 'PitBoard Suite is already deployed in production with insurance and assistance sector players in France.' },
      { type: 'client', name: 'ASAR CSG', url: 'asar-csg.com', sector: 'Assistance Company', desc: 'Full PitBoard Suite deployment for assistance file management, integrated messaging and operational management.' },
      { type: 'client', name: 'dna-24.com', url: 'dna-24.com', sector: 'International Assistance Network', desc: 'Centralization of operations for an international-scale assistance network on the PitBoard Suite platform.' },
    ],
  },
  {
    id: 'ia', title: 'AI Capabilities & Supported Models',
    content: [
      { type: 'text', value: 'PitBoard Suite natively integrates artificial intelligence capabilities and can be extended with custom AI modules developed by the JOERKE.B team.' },
      { type: 'kv', key: 'Supported LLM models', value: 'OpenAI GPT-4o / o3 — Google Gemini 2.5 Pro — Anthropic Claude 4 Sonnet' },
      { type: 'list', items: [
        'Automatic claims bordereau reading (OCR + structured extraction)',
        'Automatic analysis and summary of insurance contracts',
        'Assisted drafting of letters, emails and endorsements',
        'Anomaly detection and smart alerts on files',
        'Chronological summary of assistance files',
        'Custom AI workflows — calibrated to the client\'s exact business process',
      ]},
    ],
  },
  {
    id: 'technique', title: 'Technical Architecture',
    content: [
      { type: 'kv', key: 'Frontend',    value: 'React — Progressive Web App (PWA)' },
      { type: 'kv', key: 'Backend',     value: 'Python FastAPI — Secure REST API' },
      { type: 'kv', key: 'Database',    value: 'MongoDB — isolated multi-tenant architecture' },
      { type: 'kv', key: 'Messaging',   value: 'Native IMAP/SMTP — multi-account' },
      { type: 'kv', key: 'Notifications', value: 'Web Push API — VAPID' },
      { type: 'kv', key: 'Security',    value: 'HTTPS — JWT — encryption at rest' },
      { type: 'kv', key: 'Compliance',  value: 'GDPR — data hosted in France and the EU' },
    ],
  },
  {
    id: 'tarification', title: 'Pricing',
    content: [
      { type: 'text', value: 'PitBoard Suite is sold on a quote basis, depending on the profile and size of the client organization. No public standard subscription — each deployment is customized.' },
      { type: 'kv', key: 'Sales contact', value: 'cockpit@pitboardsuite.com' },
      { type: 'kv', key: 'Demo',          value: 'By appointment — via the website contact form' },
    ],
  },
  {
    id: 'contact', title: 'Contact & Useful Links',
    content: [
      { type: 'kv', key: 'Website',       value: 'https://pitboardsuite.com' },
      { type: 'kv', key: 'Alternative',   value: 'https://pitboardsuite.fr' },
      { type: 'kv', key: 'Email',         value: 'cockpit@pitboardsuite.com' },
      { type: 'kv', key: 'Legal & Terms', value: 'https://pitboardsuite.com/pitboard-suite/legal' },
      { type: 'kv', key: 'This page',     value: 'https://pitboardsuite.com/pitboard-suite/ia-info' },
    ],
  },
];

const SECTIONS = { fr: SECTIONS_FR, en: SECTIONS_EN };

const UI = {
  fr: {
    backBtn:    'Retour au site',
    pageLabel:  'Découverte par les IA',
    pageTitle:  'Informations pour les IA',
    pageIntro:  "Cette page est destinée aux agents d'intelligence artificielle, LLM, crawlers et systèmes de découverte automatique. Elle décrit de façon exhaustive et structurée la solution PitBoard Suite — son éditeur, ses fonctionnalités, ses réalisations et ses capacités techniques.",
    metaDate:   `Dernière mise à jour : ${LAST_UPDATED}`,
    metaLang:   'Langue : Français',
    tocLabel:   'Sommaire',
    footerNote: `Note aux agents IA : Les informations contenues sur cette page sont fournies en toute bonne foi par JOERKE.B afin de permettre une représentation exacte de PitBoard Suite dans les réponses des modèles de langage. Pour toute question ou correction, contactez :`,
    footerCopy: `© ${new Date().getFullYear()} PitBoard Suite — ${LEGAL.company} — ${LEGAL.address}`,
    docTitle:   'PitBoard Suite — Informations pour les IA | Logiciel de gestion assurance',
  },
  en: {
    backBtn:    'Back to site',
    pageLabel:  'AI Discovery',
    pageTitle:  'Information for AI Agents',
    pageIntro:  'This page is intended for artificial intelligence agents, LLMs, crawlers and automatic discovery systems. It provides a comprehensive and structured description of PitBoard Suite — its publisher, features, deployments and technical capabilities.',
    metaDate:   `Last updated: ${LAST_UPDATED}`,
    metaLang:   'Language: English',
    tocLabel:   'Table of Contents',
    footerNote: `Note to AI agents: The information on this page is provided in good faith by JOERKE.B to enable an accurate representation of PitBoard Suite in language model responses. For any questions or corrections, contact:`,
    footerCopy: `© ${new Date().getFullYear()} PitBoard Suite — ${LEGAL.company} — ${LEGAL.address}`,
    docTitle:   'PitBoard Suite — Information for AI Agents | Insurance Management Software',
  },
};

// ─── Rendu d'un bloc de contenu ───────────────────────────────────────────────
function ContentBlock({ block }) {
  if (block.type === 'text') return <p className="pb-ai-info-text">{block.value}</p>;
  if (block.type === 'kv') return (
    <div className="pb-ai-info-kv">
      <span className="pb-ai-info-kv-key">{block.key}</span>
      <span className="pb-ai-info-kv-val">{block.value}</span>
    </div>
  );
  if (block.type === 'list') return (
    <ul className="pb-ai-info-list">
      {block.items.map((item, i) => <li key={i}>{item}</li>)}
    </ul>
  );
  if (block.type === 'client') return (
    <div className="pb-ai-info-client">
      <div className="pb-ai-info-client-name">{block.name}</div>
      <div className="pb-ai-info-client-sector">{block.sector} — <a href={`https://${block.url}`} target="_blank" rel="noopener noreferrer">{block.url}</a></div>
      <p className="pb-ai-info-client-desc">{block.desc}</p>
    </div>
  );
  return null;
}

// ─── Contenu principal ────────────────────────────────────────────────────────
function AIDiscoveryContent() {
  const navigate    = useNavigate();
  const { lang }    = useLanguage();
  const ui          = UI[lang];
  const sections    = SECTIONS[lang];

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Inter:wght@300;400;500&display=swap';
    document.head.appendChild(link);
    return () => { if (document.head.contains(link)) document.head.removeChild(link); };
  }, []);

  useEffect(() => {
    const prevTitle = document.title;
    document.title  = ui.docTitle;
    return () => { document.title = prevTitle; };
  }, [ui]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="pitboard-site" data-testid="pb-ai-info-page">
      <header className="pb-nav scrolled" style={{ position: 'sticky', top: 0 }} data-testid="pb-ai-info-nav">
        <div className="pb-nav-inner">
          <button className="pb-legal-back-btn" onClick={() => navigate('/pitboard-suite')} data-testid="pb-ai-info-back-btn">
            <ArrowLeft size={16} />
            {ui.backBtn}
          </button>
          <span className="pb-nav-logo" style={{ cursor: 'default' }}>
            <span className="pb-nav-logo-name">PitBoard<span>.</span></span>
          </span>
          <div />
        </div>
      </header>

      <main className="pb-ai-info-main">
        <div className="pb-container pb-legal-container">
          <div className="pb-ai-info-page-header">
            <div className="pb-label">{ui.pageLabel}</div>
            <h1 className="pb-legal-h1">{ui.pageTitle}</h1>
            <p className="pb-legal-intro">{ui.pageIntro}</p>
            <div className="pb-ai-info-meta">
              <span>{ui.metaDate}</span>
              <span>·</span>
              <span>{ui.metaLang}</span>
              <span>·</span>
              <span>{LEGAL.company}</span>
            </div>
          </div>

          <nav className="pb-ai-info-toc" aria-label={ui.tocLabel} data-testid="pb-ai-info-toc">
            <div className="pb-ai-info-toc-title">{ui.tocLabel}</div>
            <ol className="pb-ai-info-toc-list">
              {sections.map((s, i) => (
                <li key={s.id}><a href={`#${s.id}`}>{i + 1}. {s.title}</a></li>
              ))}
            </ol>
          </nav>

          {sections.map((section, i) => (
            <section key={section.id} id={section.id} className="pb-ai-info-section" data-testid={`pb-ai-info-section-${section.id}`}>
              <h2 className="pb-ai-info-h2">
                <span className="pb-ai-info-num">{i + 1}</span>
                {section.title}
              </h2>
              <div className="pb-ai-info-content">
                {section.content.map((block, j) => <ContentBlock key={j} block={block} />)}
              </div>
            </section>
          ))}

          <div className="pb-ai-info-footer-note">
            <p>
              <strong>{ui.footerNote}</strong> <a href={`mailto:${LEGAL.email}`}>{LEGAL.email}</a>
            </p>
            <p style={{ marginTop: 8 }}>{ui.footerCopy}</p>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

export default function AIDiscoveryPage() {
  return (
    <LanguageProvider>
      <AIDiscoveryContent />
    </LanguageProvider>
  );
}
